import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaQuestionCircle, FaTimes, FaPhoneAlt, FaExternalLinkAlt } from 'react-icons/fa';
import { colors, breakpoints } from '../../../../assets/styles/GlobalStyles';

// Botão "Ajuda" fixo no canto inferior direito do wizard de Seleção e Inscrição.
// O conteúdo do cartão muda conforme a etapa: no formulário, os telefones do
// departamento; na etapa de Documentos, o link da página com os documentos
// obrigatórios e os modelos de declaração (é o mesmo link para todos os pedidos).
const TELEFONES_SELECAO_INSCRICAO = [
  { number: '(67) 3318-4711', rawNumber: '6733184711' },
  { number: '(67) 3318-4712', rawNumber: '6733184712' },
  { number: '(67) 3318-4743', rawNumber: '6733184743' },
];

const LINK_DOCUMENTACAO = 'https://oabms.org.br/secretaria-de-selecao-e-inscricao/';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  position: fixed;
  right: 24px;
  bottom: calc(24px + env(safe-area-inset-bottom));
  z-index: 900;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  // safe-area: não fica atrás da barra inferior do iOS
  @media (max-width: ${breakpoints.small}) {
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom));
    left: 16px;
  }

  > * {
    pointer-events: auto;
  }
`;

// Reserva espaço no fim da página para o botão fixo não cobrir os botões da etapa.
const Spacer = styled.div`
  height: calc(72px + env(safe-area-inset-bottom));
`;

const Card = styled.div`
  position: relative;
  width: 320px;
  max-width: 100%;
  background: ${colors.white};
  border-left: 4px solid ${colors.darkBlue};
  border-radius: 12px;
  padding: 1rem 2.75rem 1rem 1.1rem;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.2);
  animation: ${slideUp} 0.2s ease-out;
`;

const CardTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${colors.darkBlue};
  margin: 0 0 6px 0;
`;

const CardText = styled.p`
  font-size: 0.85rem;
  line-height: 1.45;
  color: ${colors.darkGray};
  margin: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: ${colors.backgroundLight};
    color: ${colors.darkBlue};
  }
`;

const PhoneList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PhoneLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${colors.darkBlue};
  text-decoration: none;

  svg {
    font-size: 0.75rem;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const DocLink = styled.a`
  display: inline;
  margin-top: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${colors.darkBlue};
  word-break: break-all;

  svg {
    font-size: 0.65rem;
    margin-left: 6px;
    vertical-align: middle;
  }
`;

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 999px;
  background: ${colors.darkBlue};
  color: ${colors.white};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(13, 35, 87, 0.3);
  transition: background 0.2s ease, transform 0.2s ease;

  svg {
    font-size: 1rem;
  }

  &:hover {
    background: ${colors.blueLight};
    transform: translateY(-1px);
  }
`;

const AjudaFlutuante = ({ etapa }) => {
  const [aberto, setAberto] = useState(false);
  const wrapperRef = useRef(null);

  // Troca de etapa fecha o cartão — o conteúdo de uma etapa não vale para a outra.
  useEffect(() => {
    setAberto(false);
  }, [etapa]);

  useEffect(() => {
    if (!aberto) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setAberto(false);
    };
    const handleClickFora = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setAberto(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickFora);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickFora);
    };
  }, [aberto]);

  return (
    <>
      <Spacer aria-hidden="true" />
      <Wrapper ref={wrapperRef}>
        {aberto && (
          <Card role="dialog" aria-labelledby="ajuda-flutuante-titulo">
            <CloseButton type="button" onClick={() => setAberto(false)} aria-label="Fechar ajuda">
              <FaTimes />
            </CloseButton>
  
            {etapa === 'documentos' ? (
              <>
                <CardTitle id="ajuda-flutuante-titulo">Dúvidas sobre a documentação?</CardTitle>
                <CardText>
                  Consulte os documentos obrigatórios e gere as declarações necessárias na página:{' '}
                  <DocLink href={LINK_DOCUMENTACAO} target="_blank" rel="noopener noreferrer">
                    {LINK_DOCUMENTACAO}
                    <FaExternalLinkAlt aria-hidden="true" />
                  </DocLink>
                </CardText>
              </>
            ) : (
              <>
                <CardTitle id="ajuda-flutuante-titulo">Dúvidas no preenchimento?</CardTitle>
                <CardText>Ligue para o Departamento de Seleção e Inscrição:</CardText>
                <PhoneList>
                  {TELEFONES_SELECAO_INSCRICAO.map(tel => (
                    <li key={tel.rawNumber}>
                      <PhoneLink href={`tel:+55${tel.rawNumber}`}>
                        <FaPhoneAlt aria-hidden="true" />
                        {tel.number}
                      </PhoneLink>
                    </li>
                  ))}
                </PhoneList>
              </>
            )}
          </Card>
        )}
  
        <TriggerButton type="button" onClick={() => setAberto(a => !a)} aria-expanded={aberto}>
          <FaQuestionCircle aria-hidden="true" />
          Ajuda
        </TriggerButton>
      </Wrapper>
    </>
  );
};

export default AjudaFlutuante;
